import React from 'react';
import { useLocation, Link as RouterLink } from 'react-router-dom';
import { Breadcrumbs, Link, Typography, Box } from '@mui/material';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { useSelector } from 'react-redux';
import routes from '../routes';
import { hasPermission } from '../utils/permissions';

const AppBreadcrumb = () => {
  const currentLocation = useLocation().pathname;
  const permissions = useSelector((state) => state.auth.permissions);

  // Same rule AppContent applies to the route itself: a crumb the user would be refused at
  // is plain text, not a link that ends in Access Denied.
  const canOpen = (pathname) => {
    const required = routes.find((route) => route.path === pathname)?.requirePermission;
    if (!required) return true;
    return [].concat(required).some((codename) => hasPermission(permissions, codename));
  };

  const getRouteName = (pathname) => {
    const currentRoute = routes.find((route) => route.path === pathname);
    return currentRoute ? currentRoute.name : false;
  };

  const getBreadcrumbs = (location) => {
    const breadcrumbs = [];
    location.split('/').reduce((prev, curr, index, array) => {
      const currentPathname = `${prev}/${curr}`;
      const routeName = getRouteName(currentPathname);
      if (routeName) {
        breadcrumbs.push({
          pathname: currentPathname,
          name: routeName,
          active: index + 1 === array.length,
        });
      }
      return currentPathname;
    });
    return breadcrumbs;
  };

  const breadcrumbs = getBreadcrumbs(currentLocation);

  return (
    <Box sx={{ mb: 2 }}>
      <Breadcrumbs
        separator={<NavigateNextIcon fontSize="small" sx={{ opacity: 0.5 }} />}
        sx={{ '& .MuiBreadcrumbs-separator': { mx: 0.5 } }}
      >
        <Link
          component={RouterLink}
          to="/"
          underline="hover"
          color="text.secondary"
          variant="caption"
          sx={{ fontWeight: 500 }}
        >
          Home
        </Link>
        {breadcrumbs.map((breadcrumb, index) =>
          breadcrumb.active || !canOpen(breadcrumb.pathname) ? (
            <Typography key={index} variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
              {breadcrumb.name}
            </Typography>
          ) : (
            <Link
              key={index}
              component={RouterLink}
              to={breadcrumb.pathname}
              underline="hover"
              color="text.secondary"
              variant="caption"
              sx={{ fontWeight: 500 }}
            >
              {breadcrumb.name}
            </Link>
          ),
        )}
      </Breadcrumbs>
    </Box>
  );
};

export default React.memo(AppBreadcrumb);
